import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getJobList, checkHealth } from "../../utils/Api";
import { useAuth } from "../../utils/AuthContext";
import { useUpload } from "../../contexts/UploadContext";
import LoadingPage from "../../components/LoadingPage";
import {
  FileText,
  Clock,
  ArrowRight,
  Activity,
  BookMarked,
  Loader2,
  CheckCircle2,
  X,
  Info,
} from "lucide-react";
import StatCard from "./components/StateCard";
import TimeSavedInfoModal from "../../modals/TimeSavedInfoModal";
import FreePlanUpgradeModal from "../../modals/FreePlanUpgradeModal";
import Tag from "../../components/Tag";

type DocumentItem = Record<string, any>;

interface DashboardStats {
  totalDocuments: number;
  totalPages: number;
  systemHealth: "Active" | "Inactive" | "Checking...";
}

const Dashboard: React.FC = () => {
  const {
    user,
    savedTimeMinutes,
    refreshSavedTime,
    needsPayment,
    accountType,
  } = useAuth();
  const { uploads } = useUpload();
  const navigate = useNavigate();
  const [documentList, setDocumentList] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalDocuments: 0,
    totalPages: 0,
    systemHealth: "Checking...",
  });
  const [showTimeSavedInfo, setShowTimeSavedInfo] = useState(false);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (
      needsPayment &&
      localStorage.getItem("upgrade_banner_dismissed") !== "true"
    ) {
      setShowUpgradeBanner(true);
    }
  }, [needsPayment]);
  const [justCompletedIds, setJustCompletedIds] = useState<Set<string>>(
    new Set(),
  );
  const completionPollRef = React.useRef<NodeJS.Timeout | null>(null);

  const fetchDocuments = async (
    background = false,
  ): Promise<DocumentItem[]> => {
    try {
      if (!background) setLoading(true);
      const { data } = await getJobList({ limit: 3, sort_order: "desc" });

      let docs: DocumentItem[] = [];
      if (data.documents && typeof data.documents === "object") {
        docs = Object.entries(data.documents).map(
          ([id, docData]: [string, any]) => ({
            ...docData,
            id,
          }),
        );
      }

      const personalDocs = docs.filter(
        (doc) =>
          !doc.organisation_id ||
          doc.user_id === user?.uid ||
          doc.is_shared_with_me,
      );

      setDocumentList(personalDocs);
      setStats((prevStats) => ({
        ...prevStats,
        totalDocuments: data.total_documents ?? 0,
        totalPages: data.total_page_count ?? 0,
      }));
      setError(null);
      return personalDocs;
    } catch (err: any) {
      setError(err.message ?? "Error fetching documents");
      setDocumentList([]);
      return [];
    } finally {
      if (!background) setLoading(false);
    }
  };

  const pollUntilTimestamped = React.useCallback(
    (completedIds: string[], attemptsLeft = 5) => {
      if (completionPollRef.current) {
        clearTimeout(completionPollRef.current);
      }
      completionPollRef.current = setTimeout(async () => {
        const docs = await fetchDocuments(true);
        const allTimestamped = completedIds.every((id) =>
          docs.some((d) => d.id === id && d.created_at),
        );
        if (!allTimestamped && attemptsLeft > 1) {
          pollUntilTimestamped(completedIds, attemptsLeft - 1);
        }
      }, 1000);
    },
    [],
  );

  const checkSystemHealth = async () => {
    try {
      const healthStatus = await checkHealth();
      setStats((prevStats) => ({
        ...prevStats,
        systemHealth: healthStatus.healthy ? "Active" : "Inactive",
      }));
    } catch (error) {
      setStats((prevStats) => ({
        ...prevStats,
        systemHealth: "Inactive",
      }));
    }
  };

  useEffect(() => {
    fetchDocuments();
    checkSystemHealth();
  }, []);

  const prevUploadsRef = React.useRef(uploads);
  useEffect(() => {
    const prev = prevUploadsRef.current;
    const newlyCompleted = uploads.filter(
      (u) =>
        u.status === "complete" &&
        u.documentId &&
        prev.find((p) => p.id === u.id)?.status !== "complete",
    );
    if (newlyCompleted.length > 0) {
      const completedIds = newlyCompleted.map((u) => u.documentId as string);

      pollUntilTimestamped(completedIds);
      setTimeout(refreshSavedTime, 1000);
      setJustCompletedIds((prev) => {
        const next = new Set(prev);
        newlyCompleted.forEach((u) => next.add(u.documentId!));
        return next;
      });
    }
    prevUploadsRef.current = uploads;
  }, [uploads]);

  const getDocumentUploadStatus = (docId: string): string | null => {
    const upload = uploads.find(
      (u) =>
        u.documentId === docId &&
        u.status !== "complete" &&
        u.status !== "error",
    );
    return upload ? upload.status : null;
  };

  // Build placeholder rows for uploads that are active and not yet in the fetched list
  const activeUploadPlaceholders: DocumentItem[] = uploads
    .filter(
      (u) =>
        u.documentId &&
        u.status !== "complete" &&
        u.status !== "error" &&
        !documentList.some((doc) => doc.id === u.documentId),
    )
    .map((u) => ({
      id: u.documentId as string,
      name: u.documentName,
      page_count: 0,
      tags: [],
      created_at: null,
      _isUploadPlaceholder: true,
    }));

  const recentDocuments = [...activeUploadPlaceholders, ...documentList].slice(
    0,
    3 + activeUploadPlaceholders.length,
  );

  return (
    <div className="bg-gray-100 h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-6 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-2xl font-semibold mb-1">Dashboard</h3>
            <p className="text-gray-500">Overview of your document workspace</p>
          </div>
          {accountType === "free" && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-gray-500">
                You're on the free plan
              </span>
              <button
                onClick={() => setShowUpgradeModal(true)}
                title="Click to find out how you could benefit from Data Revival Individual features"
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <Info size={16} className="text-gray-400" />
              </button>
              <button
                onClick={() => navigate("/upgrade")}
                className="text-sm font-medium bg-[#1d365d] hover:bg-[#16294a] text-white px-3 py-1.5 rounded transition-colors"
              >
                Upgrade
              </button>
            </div>
          )}
        </div>

        {showUpgradeBanner && (
          <div className="mb-6 flex items-center justify-between gap-4 bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 rounded-lg">
            <p className="text-sm font-medium">
              There was an issue processing your payment, so your account is now
              on the Free plan.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate("/upgrade")}
                className="text-sm font-medium bg-[#1d365d] hover:bg-[#16294a] text-white px-4 py-1.5 rounded-sm transition-colors"
              >
                Complete your upgrade
              </button>
              <button
                onClick={() => {
                  localStorage.setItem("upgrade_banner_dismissed", "true");
                  setShowUpgradeBanner(false);
                }}
                className="p-1 hover:bg-amber-100 rounded transition-colors"
                aria-label="Dismiss"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6">
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
              role="alert"
            >
              {error}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10">
        {loading ? (
          <LoadingPage
            fullScreen={false}
            variant="light"
            message="Loading dashboard data..."
          />
        ) : (
          <>
            {/* statistics cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                icon={<BookMarked size={24} />}
                title="Total Documents"
                value={stats.totalDocuments}
                iconBgColor="bg-blue-100"
                iconColor="text-blue-600"
              />
              <StatCard
                icon={<FileText size={24} />}
                title="Total Pages"
                value={stats.totalPages}
                iconBgColor="bg-green-100"
                iconColor="text-green-600"
              />
              <StatCard
                icon={<Clock size={24} />}
                title="Time Saved"
                value={
                  savedTimeMinutes < 60
                    ? `${Math.round(savedTimeMinutes)}m`
                    : `${Math.floor(savedTimeMinutes / 60)}h ${Math.round(savedTimeMinutes % 60)}m`
                }
                iconBgColor="bg-purple-100"
                iconColor="text-purple-600"
                onInfoClick={() => setShowTimeSavedInfo(true)}
              />
              <StatCard
                icon={<Activity size={24} />}
                title="System Status"
                value={stats.systemHealth}
                iconBgColor={
                  stats.systemHealth === "Active"
                    ? "bg-green-100"
                    : stats.systemHealth === "Inactive"
                      ? "bg-red-100"
                      : "bg-gray-100"
                }
                iconColor={
                  stats.systemHealth === "Active"
                    ? "text-green-600"
                    : stats.systemHealth === "Inactive"
                      ? "text-red-600"
                      : "text-gray-600"
                }
              />
            </div>

            {/* recent documents */}
            <div className="bg-white rounded shadow mb-12">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                  Recently created documents
                </h4>
                <button
                  onClick={() => navigate("/home/documents")}
                  className="text-[#1d365d] hover:text-[#2d4a75] font-medium text-sm flex items-center gap-1 transition-colors"
                >
                  View all
                  <ArrowRight size={16} />
                </button>
              </div>
              <div className="divide-y divide-gray-200">
                {recentDocuments.length === 0 ? (
                  <div className="px-4 sm:px-6 py-8 sm:py-12 text-center text-gray-500">
                    <FileText
                      size={48}
                      className="mx-auto mb-3 text-gray-400"
                    />
                    <p className="text-lg font-semibold mb-1">
                      No documents yet
                    </p>
                    <p>Upload your first document to get started</p>
                  </div>
                ) : (
                  recentDocuments.map((doc) => {
                    const uploadStatus = getDocumentUploadStatus(doc.id);
                    const uploading = uploadStatus !== null;
                    return (
                      <div
                        key={doc.id}
                        className={`px-4 sm:px-6 py-4 transition-colors flex items-start sm:items-center justify-between gap-3 ${
                          uploading
                            ? "bg-gray-50 opacity-60 cursor-default"
                            : "hover:bg-gray-50 cursor-pointer"
                        }`}
                        onClick={() => {
                          if (uploading) return;
                          setJustCompletedIds((prev) => {
                            const next = new Set(prev);
                            next.delete(doc.id);
                            return next;
                          });
                          navigate(`/view/${doc.id}`);
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          {doc.organisation_name && (
                            <span className="text-xs text-gray-500">
                              {doc.organisation_name}
                            </span>
                          )}
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h5
                              className={`font-medium truncate ${uploading ? "text-gray-500" : "text-gray-900"}`}
                            >
                              {doc.name || "Untitled Document"}
                            </h5>
                            {uploading && (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
                                <Loader2 size={12} className="animate-spin" />
                                {uploadStatus === "processing"
                                  ? "Processing..."
                                  : "Uploading..."}
                              </span>
                            )}
                            {!uploading && justCompletedIds.has(doc.id) && (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                <CheckCircle2 size={12} />
                                Complete
                              </span>
                            )}
                          </div>
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {doc.tags.map((tag: any) => (
                                <Tag
                                  key={tag.id}
                                  id={tag.id}
                                  name={tag.name}
                                  color={tag.color}
                                />
                              ))}
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <FileText size={14} />
                              {doc.page_count || 0} pages
                            </span>
                            <span className="text-xs sm:text-sm">
                              {doc.created_at
                                ? new Date(doc.created_at).toLocaleString(
                                    "en-GB",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: false,
                                    },
                                  )
                                : "Recently uploaded"}
                            </span>
                          </div>
                        </div>
                        <ArrowRight
                          size={20}
                          className="text-gray-400 flex-shrink-0 mt-1 sm:mt-0"
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <TimeSavedInfoModal
        isOpen={showTimeSavedInfo}
        setIsOpen={setShowTimeSavedInfo}
        savedTimeMinutes={savedTimeMinutes}
      />
      <FreePlanUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default Dashboard;
